import { yup } from 'common/validation/initYup';
import { ValidationSchema } from 'common/validation/ValidationSchema';
import { Type, Transform, plainToClass, classToPlain } from 'class-transformer';
import { TypeTransformDecimal } from 'common/dto/shared/types/TypeTransformDecimal';
import { Decimal } from 'decimal.js-light';
import { ApiProperty } from '@nestjs/swagger';
import type { IDto, IDtoPartial } from 'common/dto/shared/DtoInterfaces';
import { GenerateDto } from "cli/generate/decorators/GenerateDecorators";
import { SchemaLabel, SchemaPageNumber, SchemaPageSize } from "cli/generate/decorators/SchemaDecorators";
import { ColumnSortDirections } from 'client/ui/table/TableColumnSort';

@GenerateDto()
class NotificationQuery {
  @SchemaPageNumber()
  pageNumber!: number;

  @SchemaPageSize()
  pageSize!: number;
  
  @SchemaLabel("Data inizio")
  fromDate?: Date | null;

  @SchemaLabel("Data fine")
  toDate?: Date | null;

  @SchemaLabel("Ordinamento")
  orderBy?: string = 'id';

  @SchemaLabel("Direzione di Ordinamento")
  orderByDirection?: ColumnSortDirections = ColumnSortDirections.ascend;
}

/**
 * Rappresentazione DTO della classe NotificationQuery 
 * Hash: 894aae0eae3686f61f807674ff2369d3
 */
@ValidationSchema(() => NotificationQuerySchema)
export class NotificationQueryDto {
  @ApiProperty({ required: false, type: Number, description: 'Numero di pagina' })
  pageNumber!: number;
  @ApiProperty({ required: false, type: Number, description: 'Dimensione pagina' })
  pageSize!: number;
  @ApiProperty({ required: false, type: String, format: 'date-time', description: 'Data inizio' })
  fromDate?: Date | null | undefined;
  @ApiProperty({ required: false, type: String, format: 'date-time', description: 'Data fine' })
  toDate?: Date | null | undefined;
  @ApiProperty({ required: false, type: String, description: 'Ordinamento' })
  orderBy?: string | undefined = 'id';
  @ApiProperty({ required: false, enum: ['ASC', 'DESC'], description: 'Direzione di Ordinamento' })
  orderByDirection?: ColumnSortDirections | undefined = ColumnSortDirections.ascend;

  /** Proprietà che identifica i DTO */
  readonly __dto!: any;

  /**
   * Crea una nuova istanza con i valori forniti
   */
  constructor(values?: IDtoPartial<NotificationQueryDto>) {
    if (values != null) {
      Object.assign(this, values instanceof NotificationQueryDto ? values : plainToClass(NotificationQueryDto, values));
    }
  }

  async validate(options?: any) {
    const validated = await NotificationQuerySchema.validate(classToPlain(this), options);
    return new NotificationQueryDto(validated);
  }
}

export const NotificationQuerySchema = yup
  .object({
    pageNumber: yup.number().default(1).min(1).integer().positive().label('Numero di pagina'),
    pageSize: yup.number().default(20).min(10).max(50).integer().positive().label('Dimensione pagina'),
    fromDate: yup.date().nullable().label('Data inizio'),
    toDate: yup.date().nullable().label('Data fine'),
    orderBy: yup.string().default('id').label('Ordinamento'),
    orderByDirection: yup.string().oneOfEnum(ColumnSortDirections).default(ColumnSortDirections.ascend).label('Direzione di Ordinamento')
  })
  .noUnknown()
  .meta({ schemaName: "NotificationQuerySchema" })
  .required();
